import {
  closestCenter,
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  Grid,
  LinearProgress,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useTranslation } from "react-i18next";
import SimpleAlert from "../../../components/display/SimpleAlert";
import PageContentWrapper from "../../../components/wrappers/PageContentWrapper";
import { GET_BOXES, REORDER_BOXES } from "../../../enums/ApiPaths";
import { BOXES_LIST_KEY } from "../../../enums/QueryKeys";
import { useFetch } from "../../../hooks/Fetching";
import api from "../../../utils/api/authApi";
import useUser from "../../../hooks/useUser";
import { SnackbarContext } from "../../../contexts/Snackbar";
import { snackbarSeverity } from "../../../hooks/useSnackbar";
import SingleBoxCard from "../components/cards/SingleBoxCard";
import NotFound from "../components/display/NotFound";
import AddBox from "../components/forms/AddBox";
import ActiveTherapiesTable from "./ActiveTherapiesTable";
import { TabPanel } from "./single/SinglePetView";

const BoxesList = () => {
  const { roles, currentClinic } = useUser();
  const { t } = useTranslation();
  const url = GET_BOXES;
  const clinciId = currentClinic.id;
  const id = roles.master ? null : clinciId;
  const { data, isLoading, error } = useFetch(url, id, BOXES_LIST_KEY);
  const [activeTab, setActiveTab] = React.useState(0);

  return (
    <PageContentWrapper noSideMargin={true} sideMargin={5}>
      <Box sx={{ mb: 6, textAlign: "left" }}>
        <Title title={t("Stationary")} />
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{ mb: 3 }}
      >
        <Tab label={t("Boxes")} />
        <Tab label={t("ActiveTherapies")} />
      </Tabs>

      <TabPanel value={activeTab} index={0}>
        <Box mb={2}>
          <AddBox />
        </Box>
        <Data data={data} isLoading={isLoading} error={error} />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <ActiveTherapiesTable />
      </TabPanel>
    </PageContentWrapper>
  );
};

export default BoxesList;

function Data({ data = [], isLoading = false, error }) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Grid item xs={12} key={"loading-grid"}>
        <LinearProgress />
      </Grid>
    );
  } else if (error && !isLoading) {
    return (
      <SimpleAlert message={"ErrorFetchingBoxesList"} severity={"error"} />
    );
  }

  if (!data || !Array.isArray(data) || data.length == 0) {
    return <NotFound title={t("NoBoxesYet")} subtitle={t("AddFirstBox")} />;
  }
  return <SortableBoxGrid data={data} />;
}

function SortableBoxGrid({ data }) {
  const { roles } = useUser();
  const queryClient = useQueryClient();
  const [boxes, setBoxes] = React.useState(data);
  const { handleSnackbar } = React.useContext(SnackbarContext);

  React.useEffect(() => setBoxes(data), [data]);

  const reorderMutation = useMutation({
    mutationFn: (orderedBoxes) =>
      api.put(
        REORDER_BOXES,
        orderedBoxes.map((b) => b.id),
      ),
    onSuccess: (_, orderedBoxes) =>
      queryClient.setQueryData([BOXES_LIST_KEY], orderedBoxes),
    onError: (err) => {
      setBoxes(data);
      handleSnackbar(err?.response?.data, snackbarSeverity.ERROR);
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const next = arrayMove(
      boxes,
      boxes.findIndex((b) => b.id === active.id),
      boxes.findIndex((b) => b.id === over.id),
    );
    setBoxes(next);
    reorderMutation.mutate(next);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={boxes.map((b) => b.id)}
        strategy={rectSortingStrategy}
      >
        <Grid container spacing={2}>
          {boxes.map((box, index) => (
            <SortableBoxItem
              key={box.id}
              box={box}
              index={index}
              disabled={roles.master}
            />
          ))}
        </Grid>
      </SortableContext>
    </DndContext>
  );
}

function SortableBoxItem({ box, index, disabled }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: box.id, disabled });

  return (
    <Grid
      item
      xs={12}
      sm={6}
      md={4}
      lg={3}
      xl={3}
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : "auto",
        opacity: isDragging ? 0.85 : 1,
        WebkitTouchCallout: "none",
        userSelect: "none",
      }}
      {...attributes}
      {...listeners}
    >
      <SingleBoxCard box={box} index={index} />
    </Grid>
  );
}

function Title({ title = "" }) {
  const { t } = useTranslation();
  return (
    <Typography
      variant="h3"
      gutterBottom
      sx={{
        fontWeight: 800,
        mb: 2,
      }}
    >
      {t(title)}
    </Typography>
  );
}
